import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useApp } from '../context/AppContext';
import api from '../api';
import NutriScore from '../components/NutriScore';
import { Lock, RefreshCw, Save, Image as ImageIcon, CheckCircle2, LogOut, ClipboardList, UtensilsCrossed, Upload, CalendarRange, PackageCheck, AlertTriangle, Archive, Download, FileWarning, Building2, KeyRound, Plus, Copy } from 'lucide-react';

const NUTRI_COLORS = { A: '#038141', B: '#85BB2F', C: '#FECB02', D: '#EF8200', E: '#E63E11' };

export default function AdminPage() {
  const { dishes, tf, reload, isAdmin, login, logout, settings } = useApp();
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('dishes');
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [savedId, setSavedId] = useState('');
  const [syncState, setSyncState] = useState('');
  const [syncResult, setSyncResult] = useState(null);
  const [orders, setOrders] = useState([]);
  const [uploadingId, setUploadingId] = useState('');
  const [rotation, setRotation] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [rotateMsg, setRotateMsg] = useState('');
  const [markingId, setMarkingId] = useState('');
  const [health, setHealth] = useState(null);
  const [mail, setMail] = useState(null);
  const [payHealth, setPayHealth] = useState(null);
  const [adminError, setAdminError] = useState('');
  const [summaryMsg, setSummaryMsg] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState('');
  const [archive, setArchive] = useState(null);
  const [companies, setCompanies] = useState(null);
  const [newCompany, setNewCompany] = useState({ name: '', contactName: '', billingEmail: '', address: '', notes: '' });
  const [companyBusy, setCompanyBusy] = useState('');
  const [companyMsg, setCompanyMsg] = useState('');
  const [copiedCode, setCopiedCode] = useState('');
  // Default the statement to last month: on the 3rd of the month, the invoice
  // being raised is almost always the one that just closed.
  const [statementMonth, setStatementMonth] = useState(() => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const loadHealth = async () => {
    try { setHealth(await api.detailsHealth()); } catch (e) { setAdminError(`Impossible de charger l'état des fiches : ${e?.response?.status === 401 ? 'session expirée, reconnectez-vous' : (e?.response?.data?.detail || e.message)}`); }
  };
  const loadMail = async () => {
    try { setMail(await api.emailHealth()); } catch (e) { setAdminError(`Impossible de charger l'état des e-mails : ${e?.response?.status === 401 ? 'session expirée, reconnectez-vous' : (e?.response?.data?.detail || e.message)}`); }
  };
  const loadArchive = async () => {
    try { setArchive(await api.archiveIndex()); } catch (e) { setAdminError(`Impossible de charger les archives : ${e?.response?.status === 401 ? 'session expirée, reconnectez-vous' : (e?.response?.data?.detail || e.message)}`); }
  };
  const loadPayHealth = async (refresh = false) => {
    try { setPayHealth(await api.payrexxHealth(refresh)); } catch (e) { setAdminError(`Impossible de vérifier Payrexx : ${e?.response?.status === 401 ? 'session expirée, reconnectez-vous' : (e?.response?.data?.detail || e.message)}`); }
  };

  // Re-read every supplier product page. The weekly sync only sees the menu
  // currently on sale, so meals already in the fridge can otherwise never be
  // corrected — which is how a cohort sat under-declaring allergens for a week.
  const doRefreshDetails = async () => {
    if (!window.confirm("Re-lire les fiches produit chez Weekly Food ?\n\nIngrédients, allergènes et valeurs nutritionnelles seront réécrits depuis le site du fournisseur pour tous les plats affichés.")) return;
    setRefreshing(true); setRefreshMsg('');
    try {
      const res = await api.refreshDetails('all');
      const failed = (res.results || []).filter((r) => !r.ok);
      setRefreshMsg(`${res.repaired}/${res.checked} fiches relues.` + (failed.length ? ` Échecs : ${failed.map((f) => f.name).join(', ')}` : ''));
      await reload();
      await loadHealth();
    } catch (e) {
      setRefreshMsg(e?.response?.data?.detail || 'Échec de la relecture.');
    } finally {
      setRefreshing(false);
    }
  };

  const loadCompanies = async () => {
    try { setCompanies((await api.listCompanies()).companies || []); }
    catch (e) { setCompanyMsg("Impossible de charger les entreprises."); }
  };

  const addCompany = async (e) => {
    e.preventDefault();
    if (!newCompany.name.trim()) return;
    setCompanyBusy('new'); setCompanyMsg('');
    try {
      const created = await api.createCompany(newCompany);
      setNewCompany({ name: '', contactName: '', billingEmail: '', address: '', notes: '' });
      setCompanyMsg(`${created.name} créée. Code d'accès : ${created.code} — transmettez-le au contact de l'entreprise.`);
      await loadCompanies();
    } catch (e2) {
      setCompanyMsg(e2?.response?.data?.detail || "Création impossible.");
    } finally { setCompanyBusy(''); }
  };

  const patchCompany = async (id, body, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setCompanyBusy(id); setCompanyMsg('');
    try {
      const updated = await api.updateCompany(id, body);
      if (body.regenerateCode) setCompanyMsg(`Nouveau code pour ${updated.name} : ${updated.code}. L'ancien ne fonctionne plus.`);
      await loadCompanies();
    } catch (e2) {
      setCompanyMsg(e2?.response?.data?.detail || "Modification impossible.");
    } finally { setCompanyBusy(''); }
  };

  // Handing a code to an office manager usually means pasting it into an
  // e-mail, so make that one click rather than a careful retype.
  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); setCopiedCode(code); setTimeout(() => setCopiedCode(''), 2000); }
    catch (e) { /* clipboard blocked — the code is on screen anyway */ }
  };

  const money = (v) => (typeof v === 'number' ? `CHF ${v.toFixed(2)}` : '—');

  // What actually matters on this screen: has the money arrived. An order that
  // is awaiting payment is not a sale and must not be prepared.
  const payment = (o) => {
    if (o.paymentStatus === 'paid') return { label: 'Payé', cls: 'bg-[#e4f3dc] text-[#2e6b1f] border-[#bcdfae]' };
    if (o.paymentStatus === 'test_payment') return { label: 'Paiement TEST — non encaissé', cls: 'bg-red-50 text-red-700 border-red-200' };
    if (o.paymentStatus === 'amount_mismatch') return { label: 'Montant incohérent', cls: 'bg-red-50 text-red-700 border-red-200' };
    if (o.paymentProvider === 'invoice') return { label: `Facturé — ${o.companyName || 'entreprise'}`, cls: 'bg-[#eef0e6] text-[#4b5e34] border-[#d6dfc6]' };
    if (o.paymentProvider === 'payrexx') return { label: 'En attente de paiement', cls: 'bg-amber-50 text-amber-800 border-amber-200' };
    return { label: 'TWINT — à vérifier', cls: 'bg-amber-50 text-amber-800 border-amber-200' };
  };

  // TWINT is the one payment route nothing can confirm on its own, so the café
  // confirms it after checking the TWINT receipts. That click is what sends the
  // customer their receipt — so it must not be casual.
  // The same summary the café is emailed on Thursday at 09:10. Here so it can
  // be checked, and resent, without waiting a week.
  const sendSummary = async () => {
    if (!window.confirm("Envoyer le récapitulatif des précommandes de la semaine prochaine à l'adresse admin ?")) return;
    setSummaryMsg('');
    try {
      const r = await api.sendWeeklySummary();
      setSummaryMsg(`Récapitulatif envoyé à ${r.sentTo} — semaine du ${r.monday} : ${r.orders} commande(s), ${r.meals} article(s).`);
    } catch (e) {
      setSummaryMsg(e?.response?.data?.detail || "Impossible d'envoyer le récapitulatif.");
    }
  };

  const markPaid = async (o) => {
    if (!window.confirm(`Confirmer la réception du paiement TWINT de CHF ${(o.total || 0).toFixed(2)} pour ${o.ref} ?\n\nLe client recevra immédiatement sa confirmation et son reçu TVA.`)) return;
    setMarkingId(o.id);
    try {
      const res = await api.markOrderPaid(o.id, true);
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, paymentStatus: 'paid', paidAt: res.paidAt, status: 'pending' } : x)));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Impossible d'enregistrer le paiement.");
    } finally {
      setMarkingId('');
    }
  };

  const markCollected = async (o) => {
    const next = o.status === 'collected' ? 'pending' : 'collected';
    setMarkingId(o.id);
    try {
      await api.updateOrder(o.id, next);
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, status: next } : x)));
    } catch (e) {
      setErr("Impossible de mettre à jour la commande.");
    } finally {
      setMarkingId('');
    }
  };

  const loadRotation = async () => {
    try { setRotation(await api.rotationStatus()); } catch (e) { /* not fatal */ }
  };
  useEffect(() => { if (isAdmin) { loadRotation(); loadHealth(); loadArchive(); loadMail(); loadPayHealth(); } }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const REASONS = {
    already_reopened: 'précommandes déjà rouvertes ce cycle (depuis la dernière clôture).',
    supplier_unchanged: 'Weekly Food affiche encore le menu actuel — rien de nouveau à importer.',
    supplier_unavailable: 'impossible de lire weekly-food.ch pour le moment.',
    supplier_empty: 'la page Weekly Food ne renvoie aucun plat.',
    no_incoming: 'aucun menu en attente — le frigo reste sur la semaine en cours.',
  };

  const doReplacePreorderMenu = async () => {
    setRotating(true); setRotateMsg('');
    try {
      const dry = await api.replacePreorderMenu(true);
      const orders = dry.affectedOrders || [];
      const lines = [
        `Remplacer le menu de la semaine prochaine (semaine du ${dry.week2Monday || '?'}) ?`,
        '',
        `• ${dry.archived} plat(s) actuels archivés : ${(dry.archivedDishes || []).join(', ') || '—'}`,
        `• ${dry.imported} plat(s) importés depuis Weekly Food : ${(dry.importedDishes || []).join(', ')}`,
        `• Recouvrement avec le frigo actuel : ${dry.week1Overlap}`,
        '',
        orders.length
          ? `${orders.length} commande(s) concernée(s) — à recontacter :\n` + orders.map((o) => `  ${o.ref} · ${o.name} · ${o.pickupDate || '—'} · ${o.items.map((i) => `${i.qty}× ${i.name}`).join(', ')} · CHF ${(o.total || 0).toFixed(2)} · ${o.paymentStatus}`).join('\n')
          : 'Aucune commande concernée.',
        '',
        'Aucune commande n\'est modifiée. Les plats du frigo, la fenêtre de précommande et les dates ne changent pas.',
      ];
      if (!window.confirm(lines.join('\n'))) { setRotateMsg('Remplacement annulé (rien modifié).'); return; }
      const res = await api.replacePreorderMenu(false);
      setRotateMsg(`Menu remplacé — ${res.archived} archivé(s), ${res.sync?.added ?? res.imported} importé(s)${(res.affectedOrders || []).length ? ` · ${res.affectedOrders.length} commande(s) à recontacter` : ''}.`);
      await reload();
      await loadRotation();
      await loadHealth();
    } catch (e) {
      setRotateMsg(e?.response?.data?.detail || 'Échec du remplacement.');
    } finally {
      setRotating(false);
    }
  };

  const doReopen = async () => {
    if (!window.confirm("Ouvrir les précommandes de la semaine suivante ?\n\nLe menu affiché par Weekly Food est importé dans « Semaine prochaine » et les commandes rouvrent. Les plats déjà dans le frigo (« Plats de la semaine ») ne changent pas — ils basculeront samedi.\n\nÀ n'utiliser que si l'ouverture automatique du jeudi n'a pas eu lieu.")) return;
    setRotating(true); setRotateMsg('');
    try {
      const res = await api.reopen();
      setRotateMsg(res.rotated
        ? `Précommandes ouvertes — semaine prochaine = semaine du ${res.week2Monday}.`
        : `Pas d'ouverture : ${REASONS[res.reason] || res.reason}`);
      await reload();
      await loadRotation();
    } catch (e) {
      setRotateMsg(e?.response?.data?.detail || "Échec de l'ouverture.");
    } finally {
      setRotating(false);
    }
  };

  const doFridge = async () => {
    if (!window.confirm('Basculer le frigo sur le menu commandé ?\n\nLes plats commandés (en attente) deviennent « Plats de la semaine » et la semaine précédente est archivée.\n\nÀ n\'utiliser que si la bascule automatique du samedi n\'a pas eu lieu.')) return;
    setRotating(true); setRotateMsg('');
    try {
      const res = await api.rotateFridge();
      setRotateMsg(res.rotated
        ? `Frigo basculé — ${res.promoted} plat(s) désormais « au frigo ».`
        : `Pas de bascule : ${REASONS[res.reason] || res.reason}`);
      await reload();
      await loadRotation();
    } catch (e) {
      setRotateMsg(e?.response?.data?.detail || 'Échec de la bascule.');
    } finally {
      setRotating(false);
    }
  };

  const uploadPhoto = async (id, file) => {
    if (!file) return;
    setUploadingId(id);
    setErr('');
    try {
      const res = await api.uploadDishPhoto(id, file);
      setDraft((p) => ({ ...p, image: res.image }));
      await reload();
      setSavedId(id);
      setTimeout(() => setSavedId(''), 1500);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Échec de l'envoi de la photo.");
    } finally {
      setUploadingId('');
    }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setErr('');
    try { await login(pw); }
    catch (e2) { setErr('Mot de passe incorrect.'); }
  };

  const loadOrders = async () => {
    try { setOrders(await api.getOrders()); } catch (e) { setAdminError(`Impossible de charger les commandes : ${e?.response?.status === 401 ? 'session expirée, reconnectez-vous' : (e?.response?.data?.detail || e.message)}`); }
  };

  useEffect(() => { if (isAdmin && tab === 'orders') loadOrders(); }, [isAdmin, tab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (isAdmin && tab === 'companies') loadCompanies(); }, [isAdmin, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (d) => {
    setEditing(editing === d.id ? null : d.id);
    setDraft({ nutriScore: d.nutriScore, image: d.image, weight: d.weight, kcal: d.kcal });
  };

  const saveDish = async (id) => {
    try {
      await api.updateDish(id, draft);
      setSavedId(id);
      setTimeout(() => setSavedId(''), 1500);
      await reload();
    } catch (e) { setErr('Échec de l\'enregistrement.'); }
  };

  const doSync = async () => {
    setSyncState('sync'); setSyncResult(null);
    try {
      const res = await api.sync();
      setSyncResult(res);
      setSyncState('done');
      await reload();
      setTimeout(() => setSyncState(''), 6000);
    } catch (e) {
      setSyncState('error');
      setErr(e?.response?.data?.detail || 'Échec de la synchronisation.');
      setTimeout(() => setSyncState(''), 6000);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#faf9f5]">
        <Header />
        <div className="max-w-[420px] mx-auto px-5 py-24">
          <div className="bg-white rounded-2xl border border-neutral-200 p-8">
            <Lock size={28} className="text-[#4b5e34] mb-4" />
            <h1 className="font-display text-2xl text-[#2f3826] mb-1">Espace Admin</h1>
            <p className="text-sm text-neutral-500 mb-6">Connectez-vous pour gérer le menu.</p>
            <form onSubmit={doLogin} className="space-y-3">
              <input type="password" value={pw} onChange={(e) => { setPw(e.target.value); setErr(''); }} placeholder="Mot de passe" className="w-full border border-neutral-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4b5e34]/30 focus:border-[#4b5e34]" />
              {err && <p className="text-sm text-red-500">{err}</p>}
              <button className="w-full bg-[#4b5e34] hover:bg-[#3f5230] text-white py-2.5 rounded-lg font-medium transition-colors">Se connecter</button>
            </form>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf9f5]">
      <Header />
      <div className="max-w-[1280px] mx-auto px-5 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-[34px] text-[#2f3826]">Gestion du menu</h1>
            <p className="text-sm text-neutral-500">
              Synchronisez avec Weekly Food ou modifiez manuellement les plats.
              {settings?.lastSync && <> · Dernière synchro : {new Date(settings.lastSync).toLocaleString('fr-CH')}</>}
            </p>
          </div>
          <div className="flex gap-2">
            <a href="/affiches" data-testid="admin-posters-link" className="flex items-center gap-2 border border-[#4b5e34] text-[#4b5e34] text-sm px-4 py-2.5 rounded-lg hover:bg-[#eef0e6] transition-colors">
              <ClipboardList size={15} /> Affiches frigo
            </a>
            <button onClick={doSync} disabled={syncState === 'sync'} className="flex items-center gap-2 bg-[#4b5e34] hover:bg-[#3f5230] disabled:opacity-60 text-white text-sm px-4 py-2.5 rounded-lg transition-colors">
              <RefreshCw size={15} className={syncState === 'sync' ? 'animate-spin' : ''} /> Synchroniser avec Weekly Food
            </button>
            <button onClick={doReopen} disabled={rotating} data-testid="admin-reopen" className="flex items-center gap-2 border border-[#b8863b] text-[#8a6224] text-sm px-4 py-2.5 rounded-lg hover:bg-[#f6efe2] disabled:opacity-60 transition-colors">
              <CalendarRange size={15} className={rotating ? 'animate-pulse' : ''} /> Ouvrir les précommandes (jeudi)
            </button>
            <button onClick={doFridge} disabled={rotating} data-testid="admin-rotate-fridge" className="flex items-center gap-2 border border-neutral-300 text-neutral-700 text-sm px-4 py-2.5 rounded-lg hover:bg-neutral-100 disabled:opacity-60 transition-colors">
              <CalendarRange size={15} /> Basculer le frigo (samedi)
            </button>
            <button onClick={doReplacePreorderMenu} disabled={rotating} data-testid="admin-replace-preorder-menu" className="flex items-center gap-2 border border-red-300 text-red-700 text-sm px-4 py-2.5 rounded-lg hover:bg-red-50 disabled:opacity-60 transition-colors">
              <RefreshCw size={15} className={rotating ? 'animate-spin' : ''} /> Remplacer le menu de la semaine prochaine
            </button>
            <button onClick={sendSummary} data-testid="send-weekly-summary" className="flex items-center gap-2 border border-neutral-300 text-neutral-700 text-sm px-4 py-2.5 rounded-lg hover:bg-neutral-100 transition-colors">
              <ClipboardList size={15} /> Envoyer le récapitulatif
            </button>
            <button onClick={logout} className="flex items-center gap-2 border border-neutral-300 text-neutral-700 text-sm px-4 py-2.5 rounded-lg hover:bg-neutral-100 transition-colors">
              <LogOut size={15} /> Déconnexion
            </button>
          </div>
        </div>

        {syncState === 'sync' && <div className="mb-6 text-sm bg-[#eef0e6] text-[#4b5e34] rounded-lg px-4 py-3">Lecture du menu de weekly-food.ch en cours…</div>}
        {syncState === 'done' && syncResult && <div className="mb-6 text-sm bg-[#e4f3dc] text-[#2e6b1f] rounded-lg px-4 py-3 flex items-center gap-2"><CheckCircle2 size={16} /> Synchronisé : {syncResult.added} ajout(s), {syncResult.updated} mise(s) à jour ({syncResult.scraped} plats lus). Vos modifications manuelles sont préservées.</div>}
        {syncState === 'error' && <div className="mb-6 text-sm bg-red-50 text-red-600 rounded-lg px-4 py-3">{err}</div>}
        {rotateMsg && <div className="mb-6 text-sm bg-[#f6efe2] text-[#8a6224] rounded-lg px-4 py-3">{rotateMsg}</div>}
        {rotation && (
          <div data-testid="rotation-status" className="mb-6 text-xs text-neutral-600 bg-white border border-neutral-200 rounded-lg px-4 py-3 space-y-0.5">
            <p><b>Plats de la semaine</b> : semaine du {rotation.week1Monday} ({rotation.counts?.week1 ?? '?'} plats) · <b>Semaine prochaine</b> : semaine du {rotation.week2Monday} ({rotation.counts?.week2 ?? '?'} plats) — précommandes {rotation.open ? <span className="text-[#2e6b1f] font-medium">ouvertes</span> : <span className="text-amber-700 font-medium">closes</span>} (prochaine clôture {rotation.nextCutoff ? new Date(rotation.nextCutoff).toLocaleString('fr-CH', { weekday: 'long', hour: '2-digit', minute: '2-digit' }) : 'jeudi 9h00'})</p>
            {rotation.incomingMonday && <p>En attente pour le frigo (commandé, bascule samedi) : semaine du {rotation.incomingMonday} ({rotation.counts?.incoming ?? '?'} plats)</p>}
            <p>Dernière ouverture : {rotation.rotatedAt ? new Date(rotation.rotatedAt).toLocaleString('fr-CH') : 'jamais'}
              {rotation.lastRotationCheck && <> · Dernière vérification : {new Date(rotation.lastRotationCheck).toLocaleString('fr-CH')} — {rotation.lastRotationResult}</>}</p>
            <p className="text-neutral-400">Automatique : vérification chaque heure dès la clôture du jeudi matin ; les précommandes rouvrent dès que Weekly Food publie le menu suivant (jeudi matin en principe) ; le samedi 00h05, les plats commandés basculent dans le frigo.</p>
          </div>
        )}

        {adminError && (
          <div data-testid="admin-load-error" className="mb-6 rounded-xl border border-red-300 bg-red-50 px-4 py-3">
            <p className="flex items-center gap-2 text-sm font-medium text-red-800">
              <AlertTriangle size={16} /> {adminError}
            </p>
            <p className="mt-1 text-xs text-red-700">
              Une liste vide ci-dessous ne signifie donc pas qu'il n'y a rien : le chargement a échoué.
            </p>
          </div>
        )}

        {payHealth && payHealth.configured && !payHealth.ok && (
          <div data-testid="payrexx-unusable-warning" className="mb-6 rounded-xl border border-red-300 bg-red-50 px-4 py-3">
            <p className="flex items-center gap-2 text-sm font-medium text-red-800">
              <AlertTriangle size={16} /> Payrexx configuré mais inutilisable : {payHealth.error || 'raison inconnue'} — le site utilise les QR TWINT
            </p>
            <p className="mt-1 text-xs text-red-700">
              Moyens actifs : {payHealth.activeMethods?.length ? payHealth.activeMethods.join(', ') : 'aucun'}.
              Activez TWINT ou les cartes dans le tableau de bord Payrexx (mode LIVE), puis
              <button type="button" data-testid="payrexx-recheck" onClick={() => loadPayHealth(true)} className="ml-1 underline">revérifiez</button>.
            </p>
          </div>
        )}
        {payHealth && payHealth.configured && payHealth.ok && payHealth.testMode && (
          <div data-testid="payrexx-test-warning" className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
            <p className="flex items-center gap-2 text-sm font-medium text-amber-900">
              <AlertTriangle size={16} /> Payrexx en mode TEST
            </p>
            <p className="mt-1 text-xs text-amber-800">Les paiements de test ne sont pas encaissés et ne confirment aucune commande.</p>
          </div>
        )}
        {payHealth && payHealth.configured && payHealth.ok && !payHealth.testMode && (
          <div data-testid="payrexx-ok-banner" className="mb-6 rounded-xl border border-[#bcdfae] bg-[#e4f3dc] px-4 py-3">
            <p className="flex items-center gap-2 text-sm font-medium text-[#2e6b1f]">
              <CheckCircle2 size={16} /> Paiement en ligne actif : {payHealth.activeMethods?.join(', ') || '—'}
            </p>
          </div>
        )}

        {mail && mail.keyProblem && (
          <div data-testid="email-key-broken-warning" className="mb-6 rounded-xl border border-red-300 bg-red-50 px-4 py-3">
            <p className="flex items-center gap-2 text-sm font-medium text-red-800">
              <AlertTriangle size={16} /> La clé RESEND_API_KEY est inutilisable — aucun e-mail ne part
            </p>
            <p className="mt-1 text-xs text-red-700">{mail.keyProblem}</p>
            <p className="mt-1 text-xs text-red-700">
              Créez une nouvelle clé sur resend.com et copiez la valeur complète affichée à la création
              (bouton copier), pas la valeur masquée de la liste.
            </p>
          </div>
        )}
        {mail && !mail.configured && !mail.keyProblem && (
          <div data-testid="email-not-configured-warning" className="mb-6 rounded-xl border border-red-300 bg-red-50 px-4 py-3">
            <p className="flex items-center gap-2 text-sm font-medium text-red-800">
              <AlertTriangle size={16} /> Aucun e-mail n'est envoyé — ni aux clients, ni à {mail.adminEmail}
            </p>
            <p className="mt-1 text-xs text-red-700">
              La variable d'environnement <code>RESEND_API_KEY</code> n'est pas définie. Les commandes sont
              bien enregistrées et confirmées à l'écran, mais aucune notification ne part.
              {mail.failures > 0 && <> {mail.failures} tentative(s) non abouties depuis le dernier redémarrage.</>}
            </p>
            <p className="mt-1 text-xs text-red-700">
              À faire : créer une clé sur resend.com, vérifier le domaine d'envoi, puis définir
              <code> RESEND_API_KEY</code> et <code>SENDER_EMAIL</code> dans les variables d'environnement du backend.
            </p>
          </div>
        )}
        {mail && mail.configured && mail.senderIsSandbox && (
          <div data-testid="email-sandbox-warning" className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
            <p className="flex items-center gap-2 text-sm font-medium text-amber-900">
              <AlertTriangle size={16} /> Expéditeur de test (onboarding@resend.dev)
            </p>
            <p className="mt-1 text-xs text-amber-800">
              Resend n'accepte cet expéditeur que vers l'adresse du titulaire du compte : les clients ne
              recevront rien. Vérifiez un domaine et définissez <code>SENDER_EMAIL</code>.
            </p>
          </div>
        )}
        {mail && mail.configured && !mail.senderIsSandbox && mail.lastFailure && (
          <div data-testid="email-failure-warning" className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
            <p className="flex items-center gap-2 text-sm font-medium text-amber-900">
              <AlertTriangle size={16} /> Dernier échec d'envoi : {mail.lastFailure.kind} · commande {mail.lastFailure.ref || '—'}
            </p>
            <p className="mt-1 text-xs text-amber-800">{mail.lastFailure.error}</p>
          </div>
        )}

        {health && health.incomplete > 0 && (
          <div data-testid="details-health-warning" className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
            <p className="flex items-center gap-2 text-sm font-medium text-red-800">
              <FileWarning size={16} /> {health.incomplete} plat(s) avec une fiche incomplète — allergènes ou ingrédients possiblement tronqués
            </p>
            <ul className="mt-2 text-xs text-red-700 space-y-0.5">
              {health.dishes.slice(0, 10).map((d) => (
                <li key={d.id}>• {d.name} — ingrédients {d.ingredientsLength} car., allergènes : {d.allergens || '—'}</li>
              ))}
            </ul>
            <button onClick={doRefreshDetails} disabled={refreshing}
                    className="mt-3 flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm px-3 py-2 rounded-lg transition-colors">
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Re-lire les fiches produit
            </button>
          </div>
        )}
        {summaryMsg && <div data-testid="summary-msg" className="mb-6 text-sm bg-[#eef0e6] text-[#2e6b1f] rounded-lg px-4 py-3">{summaryMsg}</div>}
        {refreshMsg && <div className="mb-6 text-sm bg-[#eef0e6] text-[#2e6b1f] rounded-lg px-4 py-3">{refreshMsg}</div>}

        {/* tabs */}
        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab('dishes')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'dishes' ? 'bg-[#4b5e34] text-white' : 'bg-white border border-neutral-200 text-neutral-700'}`}><UtensilsCrossed size={15} /> Plats ({dishes.length})</button>
          <button onClick={() => setTab('orders')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'orders' ? 'bg-[#4b5e34] text-white' : 'bg-white border border-neutral-200 text-neutral-700'}`}><ClipboardList size={15} /> Commandes</button>
          <button onClick={() => setTab('companies')} data-testid="tab-companies" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'companies' ? 'bg-[#4b5e34] text-white' : 'bg-white border border-neutral-200 text-neutral-700'}`}><Building2 size={15} /> Entreprises</button>
          <button onClick={() => setTab('archives')} data-testid="tab-archives" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'archives' ? 'bg-[#4b5e34] text-white' : 'bg-white border border-neutral-200 text-neutral-700'}`}><Archive size={15} /> Archives</button>
        </div>

        {tab === 'dishes' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dishes.map((d) => (
              <div key={d.id} className="bg-white rounded-2xl border border-neutral-200 p-4">
                <div className="flex gap-3">
                  <img src={d.image} alt="" className="w-16 h-16 rounded-xl object-cover bg-neutral-100" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-[#2f3826] truncate">{tf(d.name)}</p>
                    <p className="text-xs text-neutral-400">{d.category} · {d.availability} · <span className="uppercase">{d.source}</span></p>
                    <div className="mt-1"><NutriScore score={d.nutriScore} size={22} /></div>
                  </div>
                </div>
                <button onClick={() => startEdit(d)} className="mt-3 w-full text-sm border border-neutral-300 rounded-lg py-1.5 hover:bg-neutral-100 transition-colors">
                  {editing === d.id ? 'Fermer' : 'Modifier'}
                </button>

                {editing === d.id && (
                  <div className="mt-3 space-y-2 border-t border-neutral-100 pt-3">
                    <label className="text-xs text-neutral-500">Nutri-Score</label>
                    <div className="flex gap-1.5">
                      {['A', 'B', 'C', 'D', 'E'].map((s) => (
                        <button key={s} onClick={() => setDraft((p) => ({ ...p, nutriScore: s }))}
                          className={`w-8 h-8 rounded-full text-xs font-bold ${draft.nutriScore === s ? 'ring-2 ring-offset-1 ring-[#4b5e34]' : ''}`}
                          style={{ background: NUTRI_COLORS[s], color: s === 'C' ? '#3a2e00' : '#fff' }}>{s}</button>
                      ))}
                    </div>
                    <label className="text-xs text-neutral-500 flex items-center gap-1 mt-2"><ImageIcon size={13} /> URL de l'image</label>
                    <input value={draft.image || ''} onChange={(e) => setDraft((p) => ({ ...p, image: e.target.value }))}
                      className="w-full border border-neutral-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#4b5e34]" />
                    <label data-testid={`upload-photo-${d.id}`} className="w-full flex items-center justify-center gap-1.5 border border-dashed border-[#4b5e34] text-[#4b5e34] text-xs py-2 rounded-lg cursor-pointer hover:bg-[#eef0e6] transition-colors">
                      <Upload size={13} /> {uploadingId === d.id ? 'Envoi en cours…' : 'Téléverser une photo'}
                      <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" disabled={uploadingId === d.id}
                        onChange={(e) => { uploadPhoto(d.id, e.target.files[0]); e.target.value = ''; }} />
                    </label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="text-xs text-neutral-500">Poids (g)</label>
                        <input type="number" value={draft.weight ?? ''} onChange={(e) => setDraft((p) => ({ ...p, weight: parseFloat(e.target.value) }))}
                          className="w-full border border-neutral-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#4b5e34]" />
                      </div>
                    </div>
                    <button onClick={() => saveDish(d.id)} className="w-full flex items-center justify-center gap-1.5 bg-[#4b5e34] text-white text-sm py-1.5 rounded-lg mt-1">
                      {savedId === d.id ? <><CheckCircle2 size={14} /> Enregistré</> : <><Save size={14} /> Enregistrer</>}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === 'companies' && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-neutral-200 p-5">
              <h3 className="font-display text-lg text-[#2f3826] mb-1">Entreprises clientes</h3>
              <p className="text-sm text-neutral-500 mb-4">
                Un code d'accès par entreprise. Toute personne de l'entreprise qui le possède peut commander&nbsp;;
                son nom est enregistré sur chaque commande et figure sur le relevé mensuel.
                Les commandes entreprise ne sont pas payées en ligne — elles sont facturées.
              </p>

              <form onSubmit={addCompany} data-testid="company-create-form" className="grid sm:grid-cols-2 gap-3 mb-5 bg-[#f7f7f3] rounded-xl p-4">
                <input value={newCompany.name} onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                       data-testid="company-name" placeholder="Nom de l'entreprise *" required
                       className="border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                <input value={newCompany.contactName} onChange={(e) => setNewCompany({ ...newCompany, contactName: e.target.value })}
                       placeholder="Personne de contact" className="border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                <input value={newCompany.billingEmail} onChange={(e) => setNewCompany({ ...newCompany, billingEmail: e.target.value })}
                       type="email" placeholder="E-mail de facturation" className="border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                <input value={newCompany.address} onChange={(e) => setNewCompany({ ...newCompany, address: e.target.value })}
                       placeholder="Adresse de facturation" className="border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                <textarea value={newCompany.notes} onChange={(e) => setNewCompany({ ...newCompany, notes: e.target.value })}
                          placeholder="Notes (conditions, contact secondaire…)" rows={2}
                          className="sm:col-span-2 border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                <div className="sm:col-span-2">
                  <button type="submit" disabled={companyBusy === 'new' || !newCompany.name.trim()}
                          data-testid="company-create-submit"
                          className="flex items-center gap-2 bg-[#4b5e34] hover:bg-[#3f5230] disabled:opacity-50 text-white text-sm px-4 py-2 rounded-lg transition-colors">
                    <Plus size={15} /> Créer l'entreprise et générer un code
                  </button>
                </div>
              </form>

              {companyMsg && <div data-testid="company-msg" className="mb-4 text-sm bg-[#eef0e6] text-[#2e6b1f] rounded-lg px-4 py-3">{companyMsg}</div>}

              <div className="flex flex-wrap items-center gap-2 mb-4 text-sm">
                <label className="text-neutral-500">Relevé du mois</label>
                <input type="month" value={statementMonth} onChange={(e) => setStatementMonth(e.target.value)}
                       data-testid="statement-month"
                       className="border border-neutral-300 rounded-lg px-3 py-1.5 text-sm" />
              </div>

              {companies === null ? (
                <p className="text-sm text-neutral-400">Chargement…</p>
              ) : companies.length === 0 ? (
                <p className="text-sm text-neutral-500">Aucune entreprise pour l'instant.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="companies-table">
                    <thead className="bg-neutral-50 text-neutral-500 text-left text-xs uppercase">
                      <tr>
                        <th className="px-3 py-2">Entreprise</th>
                        <th className="px-3 py-2">Code d'accès</th>
                        <th className="px-3 py-2">Facturation</th>
                        <th className="px-3 py-2">Cmd.</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {companies.map((c) => (
                        <tr key={c.id} className={`border-t border-neutral-100 ${c.active ? '' : 'opacity-50'}`}>
                          <td className="px-3 py-2.5">
                            <p className="font-medium text-[#2f3826]">{c.name}</p>
                            {c.contactName && <p className="text-xs text-neutral-400">{c.contactName}</p>}
                            {!c.active && <p className="text-xs text-red-600">Désactivée — le code ne fonctionne plus</p>}
                          </td>
                          <td className="px-3 py-2.5">
                            <button onClick={() => copyCode(c.code)} title="Copier"
                                    className="flex items-center gap-1.5 font-mono text-[13px] tracking-wide text-[#2f3826] hover:text-[#4b5e34]">
                              {c.code} {copiedCode === c.code ? <CheckCircle2 size={13} className="text-[#4b5e34]" /> : <Copy size={13} className="text-neutral-400" />}
                            </button>
                          </td>
                          <td className="px-3 py-2.5 text-xs text-neutral-500">{c.billingEmail || '—'}</td>
                          <td className="px-3 py-2.5">{c.orders ?? 0}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex flex-wrap justify-end gap-2">
                              <button onClick={() => api.downloadExport(`/admin/companies/${c.id}/statement?fmt=csv&month=${statementMonth}`, `metio-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 16)}-${statementMonth}.csv`)}
                                      data-testid={`statement-${c.id}`}
                                      className="flex items-center gap-1.5 border border-[#4b5e34] text-[#4b5e34] text-xs px-2.5 py-1.5 rounded-lg hover:bg-[#eef0e6] transition-colors">
                                <Download size={13} /> Relevé
                              </button>
                              <button onClick={() => patchCompany(c.id, { regenerateCode: true }, `Générer un nouveau code pour ${c.name} ?\n\nL'ancien code cessera immédiatement de fonctionner et toute session ouverte sera fermée.`)}
                                      disabled={companyBusy === c.id}
                                      className="flex items-center gap-1.5 border border-neutral-300 text-neutral-700 text-xs px-2.5 py-1.5 rounded-lg hover:bg-neutral-100 disabled:opacity-50 transition-colors">
                                <KeyRound size={13} /> Nouveau code
                              </button>
                              <button onClick={() => patchCompany(c.id, { active: !c.active }, c.active ? `Désactiver ${c.name} ? Le code cessera de fonctionner.` : '')}
                                      disabled={companyBusy === c.id}
                                      className="border border-neutral-300 text-neutral-700 text-xs px-2.5 py-1.5 rounded-lg hover:bg-neutral-100 disabled:opacity-50 transition-colors">
                                {c.active ? 'Désactiver' : 'Réactiver'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'archives' && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-neutral-200 p-5">
              <h3 className="font-display text-lg text-[#2f3826] mb-1">Menus archivés</h3>
              <p className="text-sm text-neutral-500 mb-4">
                Rien n'est supprimé : chaque semaine passe en archive avec ses plats, prix, allergènes et valeurs nutritionnelles.
                Depuis la bascule du samedi, la semaine servie est enregistrée avec le plat.
              </p>
              <div className="flex flex-wrap gap-2 mb-5">
                <button onClick={() => api.downloadExport('/admin/archive/export?fmt=csv', 'metio-menus-archive.csv')}
                        data-testid="export-menus-csv"
                        className="flex items-center gap-2 border border-[#4b5e34] text-[#4b5e34] text-sm px-3 py-2 rounded-lg hover:bg-[#eef0e6] transition-colors">
                  <Download size={14} /> Tous les menus (CSV)
                </button>
                <button onClick={() => api.downloadExport('/admin/archive/export?fmt=json&include_current=true', 'metio-menus-complet.json')}
                        className="flex items-center gap-2 border border-neutral-300 text-neutral-700 text-sm px-3 py-2 rounded-lg hover:bg-neutral-100 transition-colors">
                  <Download size={14} /> Tout, actuel inclus (JSON)
                </button>
                <button onClick={() => api.downloadExport('/admin/archive/orders?fmt=csv', 'metio-commandes.csv')}
                        data-testid="export-orders-csv"
                        className="flex items-center gap-2 border border-neutral-300 text-neutral-700 text-sm px-3 py-2 rounded-lg hover:bg-neutral-100 transition-colors">
                  <Download size={14} /> Commandes (CSV)
                </button>
              </div>

              {!archive ? (
                <p className="text-sm text-neutral-400">Chargement…</p>
              ) : archive.weeks.length === 0 ? (
                <p className="text-sm text-neutral-500">Aucune semaine archivée pour l'instant.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 text-neutral-500 text-left text-xs uppercase">
                    <tr><th className="px-3 py-2">Semaine du</th><th className="px-3 py-2">Plats</th><th className="px-3 py-2">Catégories</th><th className="px-3 py-2">Archivé le</th><th className="px-3 py-2"></th></tr>
                  </thead>
                  <tbody>
                    {archive.weeks.map((w) => (
                      <tr key={w.weekMonday} className="border-t border-neutral-100">
                        <td className="px-3 py-2 font-medium text-[#2f3826]">{w.weekMonday === 'undated' ? 'Avant l\'horodatage' : w.weekMonday}</td>
                        <td className="px-3 py-2">{w.dishes}</td>
                        <td className="px-3 py-2 text-neutral-500 text-xs">{Object.entries(w.categories).map(([c, n]) => `${c} ${n}`).join(' · ')}</td>
                        <td className="px-3 py-2 text-xs text-neutral-400">{w.archivedAt ? new Date(w.archivedAt).toLocaleDateString('fr-CH') : '—'}</td>
                        <td className="px-3 py-2 text-right">
                          {w.weekMonday !== 'undated' && (
                            <button onClick={() => api.downloadExport(`/admin/archive/export?fmt=csv&week=${w.weekMonday}`, `metio-menu-${w.weekMonday}.csv`)}
                                    className="text-xs text-[#4b5e34] hover:underline">CSV</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="mt-4 text-xs text-neutral-400">Total archivé : {archive?.totalArchived ?? '—'} articles.</p>
            </div>
          </div>
        )}

        {tab === 'orders' && (
          <div className="space-y-4">
            {orders.length > 0 && (() => {
              const paid = orders.filter((o) => o.paymentStatus === 'paid');
              const waiting = orders.filter((o) => o.paymentStatus !== 'paid'
                && ['payrexx', 'twint_qr'].includes(o.paymentProvider)
                && o.status !== 'cancelled');
              const takings = paid.reduce((a, o) => a + (o.total || 0), 0);
              return (
                <div data-testid="orders-summary" className="flex flex-wrap gap-4 text-sm bg-white border border-neutral-200 rounded-xl px-4 py-3">
                  <span><b>{orders.length}</b> commande(s)</span>
                  <span className="text-[#2e6b1f]"><b>{paid.length}</b> payée(s) · {money(takings)} encaissé</span>
                  {waiting.length > 0 && (
                    <span className="text-amber-800 flex items-center gap-1">
                      <AlertTriangle size={14} /> <b>{waiting.length}</b> en attente de paiement — vérifiez vos reçus TWINT, puis cliquez sur « TWINT — à vérifier » pour confirmer et envoyer le reçu
                    </span>
                  )}
                </div>
              );
            })()}

            <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
              {orders.length === 0 ? (
                <div className="p-10 text-center text-neutral-500 text-sm">Aucune commande pour le moment.</div>
              ) : (
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 text-neutral-500 text-left text-xs uppercase">
                    <tr>
                      <th className="px-4 py-3">Réf.</th><th className="px-4 py-3">Paiement</th>
                      <th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Client</th>
                      <th className="px-4 py-3">Retrait</th><th className="px-4 py-3">Articles</th>
                      <th className="px-4 py-3">Commandé le</th><th className="px-4 py-3">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => {
                      const pay = payment(o);
                      const done = o.status === 'collected';
                      const cancelled = o.status === 'cancelled';
                      // Awaiting money, whichever route: a Payrexx order nobody paid,
                      // or a TWINT order whose payment has not been checked yet.
                      const unpaid = o.paymentStatus !== 'paid'
                        && ['payrexx', 'twint_qr'].includes(o.paymentProvider)
                        && !cancelled;
                      const needsCheck = o.paymentProvider === 'twint_qr'
                        && o.paymentStatus !== 'paid' && !cancelled;
                      return (
                        <tr key={o.id} data-testid={`order-row-${o.ref}`}
                            className={`border-t border-neutral-100 align-top ${unpaid ? 'bg-amber-50/40' : ''} ${done || cancelled ? 'opacity-55' : ''} ${cancelled ? 'line-through' : ''}`}>
                          <td className="px-4 py-3 font-mono text-[#4b5e34] font-semibold whitespace-nowrap">{o.ref}</td>
                          <td className="px-4 py-3">
                            {/* The confirm button also lives in the Actions column, which is
                                off-screen on a narrow window — so the badge itself confirms the
                                payment when one is waiting. Same guard rail, same dialog. */}
                            {needsCheck ? (
                              <button
                                onClick={() => markPaid(o)}
                                disabled={markingId === o.id}
                                data-testid={`pay-badge-${o.ref}`}
                                title="Paiement vérifié dans vos reçus TWINT ? Cliquez pour confirmer la commande."
                                className={`inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium px-2 py-1 rounded-full border hover:brightness-95 disabled:opacity-50 transition ${pay.cls}`}
                              >
                                <CheckCircle2 size={13} /> {pay.label} — confirmer
                              </button>
                            ) : (
                              <span className={`inline-block whitespace-nowrap text-xs font-medium px-2 py-1 rounded-full border ${pay.cls}`}>{pay.label}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{money(o.total)}</td>
                          <td className="px-4 py-3">{o.name || '—'}<div className="text-xs text-neutral-400">{o.email || o.phone}</div></td>
                          <td className="px-4 py-3 text-neutral-600">
                            {o.pickupDate || '—'}
                            <div className="text-xs text-neutral-400">{o.building || o.pickupName}</div>
                          </td>
                          <td className="px-4 py-3 text-neutral-600">
                            {(o.items || []).map((i, n) => (
                              <div key={n} className="whitespace-nowrap">{i.qty}× {i.name}</div>
                            ))}
                            {o.notes && <div className="text-xs text-neutral-400 mt-1 italic">{o.notes}</div>}
                          </td>
                          <td className="px-4 py-3 text-xs text-neutral-400 whitespace-nowrap">{new Date(o.createdAt).toLocaleString('fr-CH')}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1.5 items-start">
                              {needsCheck && (
                                <button
                                  onClick={() => markPaid(o)}
                                  disabled={markingId === o.id}
                                  data-testid={`mark-paid-${o.ref}`}
                                  title="Après avoir vérifié le paiement dans vos reçus TWINT"
                                  className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-[#4b5e34] bg-[#4b5e34] text-white hover:bg-[#3f5230] disabled:opacity-50 transition-colors"
                                >
                                  <CheckCircle2 size={14} /> Paiement reçu
                                </button>
                              )}
                              {cancelled ? (
                                <span className="text-xs text-red-600">Annulée — non payée</span>
                              ) : (
                                <button
                                  onClick={() => markCollected(o)}
                                  disabled={markingId === o.id}
                                  data-testid={`collect-${o.ref}`}
                                  className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors disabled:opacity-50 ${
                                    done ? 'border-[#4b5e34] bg-[#eef0e6] text-[#4b5e34]' : 'border-neutral-300 text-neutral-600 hover:bg-neutral-100'
                                  }`}
                                >
                                  <PackageCheck size={14} /> {done ? 'Retirée' : 'Marquer retirée'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
